/**
 * Created by piotr.pozniak@thebeaverhead.com on 15/02/2023.
 * Rebuilt to mirror CalendarsPage.js's architecture
 * (design_handoff_calendars_index), minus "Copy shareable link" (alerts
 * have no public shareable URL) and the Template filter (only one template
 * is meaningfully in use today).
 */

import React, { useCallback, useEffect, useRef, useState } from "react";

import { useHistory } from "react-router-dom";
import PageTemplate from "./../templates/PageTemplate";
import { getAlertCurrentData } from "../../../actions/alert";
import { useAlertsStore } from "../../../hooks/redux/alerts";
import { useAlertStore } from "../../../hooks/redux/alert";
import { formatError } from "../../../helpers/errors";
import { updateJquery } from "../tools";
import { useCurrentOrganizationStore } from "../../../hooks/redux/currentOrganization";
import { WT } from "../consts/widget";
import { useSortableTable } from "../component/SortableTable/useSortableTable";
import { useSortableTablesStore } from "../../../hooks/redux/sortableTables";
import { useDebounce } from "../../../hooks/useDebounce";
import Button from "../theme/untitled/Button/Button";
import Card from "../theme/untitled/Card/Card";
import EmptyState from "../theme/untitled/EmptyState/EmptyState";
import Icon from "../theme/untitled/icons/Icon";
import Pagination from "../theme/untitled/Pagination/Pagination";
import { ToastProvider, useToast } from "../theme/untitled/Toast/ToastProvider";
import StatusBadge from "../component/WidgetIndex/StatusBadge";
import Footer from "../component/WidgetIndex/Footer";
import MajorToast from "../component/Toast/MajorToast";
import DeleteModal from "../component/WidgetIndex/DeleteModal";
import RowErrorStrip from "../component/WidgetIndex/RowErrorStrip";
import NoResultsState from "../component/WidgetIndex/NoResultsState";
import AlertsTable from "./AlertsPage/Table";
import RowMenu from "../component/WidgetIndex/RowMenu";
import Toolbar from "../component/WidgetIndex/Toolbar";
import { useAlertsFilterSort } from "./AlertsPage/useAlertsFilterSort";
import { usePublishState } from "../component/WidgetIndex/usePublishState";
import { PAGE_SIZE, SORT_COLUMNS, getEditUrl } from "./AlertsPage/constants";
import styles from "./AlertsPage.module.scss";

// useToast() needs a ToastProvider ancestor, which can't be the same
// component instance that calls the hook — so the actual page content
// lives in this inner component, wrapped by the default export below.
const AlertsPageContent = (props) => {
  const history = useHistory();
  const { alerts, fetchAlerts } = useAlertsStore();
  const { alert, updateAlert, setAlert, deleteAlert } = useAlertStore();
  const { currentOrganization } = useCurrentOrganizationStore();
  const showToast = useToast();
  const { sortableTables, setWidgetsSorting } = useSortableTablesStore();

  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search, 200);
  const [statusValues, setStatusValues] = useState([]);

  const hasActiveFilters = !!debouncedSearch.trim() || statusValues.length > 0;

  const filteredData = useAlertsFilterSort(alerts.collection, {
    search: debouncedSearch,
    statusValues,
  });

  const { sortedData } = useSortableTable(
    filteredData,
    SORT_COLUMNS,
    sortableTables.columns[WT.alerts].field,
    sortableTables.columns[WT.alerts].order
  );

  const [page, setPage] = useState(1);
  const pageCount = Math.max(1, Math.ceil(sortedData.length / PAGE_SIZE));
  const pagedData = sortedData.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const tableTopRef = useRef(null);
  const [liveAnnouncement, setLiveAnnouncement] = useState("");

  // Page resets whenever the set being paginated changes shape, so a user
  // can't land on an out-of-range page after narrowing the list further.
  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, statusValues]);

  const onPageChange = useCallback(
    (nextPage) => {
      setPage(nextPage);
      tableTopRef.current?.focus();
      const start = (nextPage - 1) * PAGE_SIZE + 1;
      const end = Math.min(nextPage * PAGE_SIZE, sortedData.length);
      setLiveAnnouncement(`Showing ${start} to ${end} of ${sortedData.length} alerts`);
    },
    [sortedData.length]
  );

  const clearFilters = useCallback(() => {
    setSearch("");
    setStatusValues([]);
  }, []);

  useEffect(() => {
    fetchAlerts();
  }, []);

  useEffect(() => {
    /**
     * If no alerts available, navigate to new alert page.
     * Handle back button so user does not enter an infinite loop.
     */
    if (alerts.fetchSuccess && !alerts.collection.length) {
      if (history.action === "POP" && props) {
        history.go(-1);
      } else {
        history.push("/alert/new");
      }
    }
    updateJquery();
  }, [alerts.fetchSuccess]);

  const { pendingUuid, failedRow, togglePublish, dismissError } = usePublishState({
    item: alert,
    updateItem: updateAlert,
    setItem: setAlert,
    getCurrentData: getAlertCurrentData,
  });

  // Duplicate shares the same `alert.updateError`/`updateSuccess` signal as
  // publish (both dispatch through updateAlert), but isn't covered by the
  // row error strip above (that's publish-only) — this is its own
  // pending-tracker so a Duplicate outcome still surfaces, without
  // double-firing alongside a publish outcome that's already being shown
  // inline. See usePublishState.js for why a watch-effect (not try/catch)
  // is required here: updateAlert's own promise never rejects. Holds the
  // source alert's name (not just a boolean) so the toast can name it.
  const cloningRef = useRef(null);

  const onDuplicate = useCallback(
    (alert) => {
      const alertData = getAlertCurrentData(alert);
      alertData.uuid = null;
      alertData.available = 0;
      alertData.name = "Copy of " + alertData.name;

      cloningRef.current = { name: alert.name };
      updateAlert(null, alertData);
    },
    [updateAlert]
  );

  useEffect(() => {
    if (!cloningRef.current) {
      return;
    }

    if (alert.updateError) {
      showToast({
        variant: "error",
        message: `Could not duplicate "${cloningRef.current.name}". ${formatError(
          alert.updateError
        ).join(" ")}`,
      });
      cloningRef.current = null;
    } else if (alert.updateSuccess) {
      showToast({ variant: "success", message: `"${cloningRef.current.name}" duplicated.` });
      cloningRef.current = null;
      setAlert(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [alert.updateError, alert.updateSuccess]);

  // Delete: only one alert can be mid-delete at a time, and the
  // confirm/deleting/failed states map directly onto the `alert` (singular)
  // reducer's own delete/deleteError/deleteSuccess flags — already
  // correctly wired, no reducer changes needed.
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deletedToastName, setDeletedToastName] = useState(null);
  const deleteToastTimeoutRef = useRef(null);

  const onConfirmDelete = useCallback((target) => deleteAlert(target.uuid), [deleteAlert]);

  const dismissDeleteToast = useCallback(() => {
    clearTimeout(deleteToastTimeoutRef.current);
    setDeletedToastName(null);
  }, []);

  useEffect(() => {
    if (!deleteTarget) {
      return;
    }
    
    if (alert.deleteSuccess) {
      setDeletedToastName(deleteTarget.name);
      clearTimeout(deleteToastTimeoutRef.current);
      deleteToastTimeoutRef.current = setTimeout(() => setDeletedToastName(null), 6000);
      setDeleteTarget(null);
      setAlert(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [alert.deleteSuccess]);

  useEffect(() => () => clearTimeout(deleteToastTimeoutRef.current), []);

  const onCreateAlert = useCallback(() => history.push("/alert/new"), [history]);

  const isMutating = alerts.fetch;
  const isInitialLoading = alerts.fetch && !alerts.collection.length;

  const sortBy = sortableTables.columns[WT.alerts].field
    ? {
        key: sortableTables.columns[WT.alerts].field,
        direction: sortableTables.columns[WT.alerts].order || "asc",
      }
    : undefined;

  return (
    <PageTemplate
      sidebarId="alerts"
      title="Event Alerts"
      subtitle={`${currentOrganization.model.name} alert widgets.`}
      icon={"campaign"}
      headerControls={[
        <Button
          key="create-alert"
          iconLeading={<Icon name="Plus" />}
          onClick={onCreateAlert}
        >
          Create alert
        </Button>,
      ]}
    >
      <Card className={styles.card}>
        <Toolbar
          search={search}
          onSearchChange={setSearch}
          statusValues={statusValues}
          onStatusChange={setStatusValues}
          itemLabelPlural="alerts"
        />
        <Card.Body noPadding>
          <div
            className={styles.tableScroll}
            ref={tableTopRef}
            tabIndex={-1}
            aria-label="Alerts table"
          >
            {!isInitialLoading && !alerts.collection.length && !hasActiveFilters ? (
              <EmptyState
                icon={<Icon name="Announcement01" size={24} />}
                title="No alert widgets yet"
                body="Create your first alert widget to get started."
              />
            ) : !isInitialLoading && sortedData.length === 0 ? (
              <NoResultsState onClear={clearFilters} itemLabelPlural="alerts" />
            ) : (
              <AlertsTable
                loading={isInitialLoading}
                rows={pagedData}
                sortBy={sortBy}
                onSortChange={({ key, direction }) =>
                  setWidgetsSorting(WT.alerts, key, direction)
                }
                isRowPublishing={(row) => pendingUuid === row.uuid}
                renderStatus={(row) => (
                  <StatusBadge item={row} isPublishing={pendingUuid === row.uuid} />
                )}
                renderMenu={(row) => (
                  <RowMenu
                    item={row}
                    itemLabel="alert"
                    editUrl={getEditUrl(row)}
                    onDuplicate={onDuplicate}
                    onTogglePublish={togglePublish}
                    onDelete={setDeleteTarget}
                    disabled={isMutating}
                  />
                )}
                rowExtra={(row) =>
                  failedRow?.uuid === row.uuid ? (
                    <RowErrorStrip
                      item={row}
                      message={failedRow.message}
                      onRetry={() => togglePublish(row)}
                      onDismiss={dismissError}
                      colSpan={5}
                    />
                  ) : null
                }
              />
            )}
          </div>
        </Card.Body>
        <div aria-live="polite" className="sr-only">
          {liveAnnouncement}
        </div>
        <Footer
          currentOrganization={currentOrganization}
          itemLabel="alert"
          publishedCount={alerts.collection.filter((a) => a.available === 1).length}
          rangeStart={pagedData.length ? (page - 1) * PAGE_SIZE + 1 : 0}
          rangeEnd={(page - 1) * PAGE_SIZE + pagedData.length}
          total={sortedData.length}
          totalCount={alerts.collection.length}
          pagination={
            sortedData.length > PAGE_SIZE ? (
              <Pagination variant="numbered" page={page} totalPages={pageCount} onPageChange={onPageChange} />
            ) : null
          }
        />
      </Card>
      <DeleteModal
        item={deleteTarget}
        itemLabel="alert"
        deleting={alert.delete}
        deleteErrorMessage={alert.deleteError ? formatError(alert.deleteError).join(" ") : null}
        onConfirm={onConfirmDelete}
        onCancel={() => setDeleteTarget(null)}
      />
      {deletedToastName && (
        <MajorToast
          title={deletedToastName}
          text="Its embed has stopped showing alerts."
          onDismiss={dismissDeleteToast}
        />
      )}
    </PageTemplate>
  );
};

const AlertsPage = (props) => (
  <ToastProvider>
    <AlertsPageContent {...props} />
  </ToastProvider>
);

export default AlertsPage;
